import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { FiCheck, FiX, FiEye, FiRefreshCw, FiCalendar, FiUser, FiPhone, FiMail, FiMapPin, FiDollarSign, FiTrash2, FiHome, FiClock, FiRotateCw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { formatarData } from '../../utils/dateUtils';
import { FORMAS_PAGAMENTO, formaIndicaCarne } from '../../constants/formasPagamento';

const PreReservas = () => {
  const [preReservas, setPreReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [preReservaSelecionada, setPreReservaSelecionada] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [buscandoCpfExistente, setBuscandoCpfExistente] = useState(false);
  const [resultadoBuscaCpf, setResultadoBuscaCpf] = useState(null);
  const [cpfNaoEncontrado, setCpfNaoEncontrado] = useState(false);
  const [erroBuscaCpf, setErroBuscaCpf] = useState('');
  const ultimoCpfConsultadoRef = useRef('');
  const buscaCpfTimeoutRef = useRef(null);
  const [formAprovacao, setFormAprovacao] = useState({
    forma_pagamento: '',
    valor: '',
    status_pagamento: 'pendente',
    cpf: '',
    telefone: '',
    numero_parcelas: ''
  });

  useEffect(() => {
    fetchPreReservas();
  }, [filtroStatus]);

  useEffect(() => {
    return () => {
      if (buscaCpfTimeoutRef.current) {
        clearTimeout(buscaCpfTimeoutRef.current);
      }
    };
  }, []);

  const normalizarCPF = (cpf) => (cpf || '').replace(/\D/g, '');

  const formatarCPF = (cpf) => {
    const cpfLimpo = normalizarCPF(cpf);
    if (cpfLimpo.length <= 11) {
      return cpfLimpo.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cpfLimpo.slice(0, 11).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const buscarCpfExistente = async (cpfFormatado) => {
    const cpfLimpo = normalizarCPF(cpfFormatado);

    if (cpfLimpo.length !== 11) {
      setResultadoBuscaCpf(null);
      setCpfNaoEncontrado(false);
      setErroBuscaCpf('');
      ultimoCpfConsultadoRef.current = '';
      return;
    }

    if (cpfLimpo === ultimoCpfConsultadoRef.current) {
      return;
    }

    setBuscandoCpfExistente(true);
    setErroBuscaCpf('');

    try {
      const response = await api.get(`/api/passageiros/buscar/cpf/${encodeURIComponent(cpfLimpo)}`);
      setResultadoBuscaCpf(response.data || null);
      setCpfNaoEncontrado(false);
      ultimoCpfConsultadoRef.current = cpfLimpo;
    } catch (error) {
      if (error.response?.status === 404) {
        setResultadoBuscaCpf(null);
        setCpfNaoEncontrado(true);
        setErroBuscaCpf('');
        ultimoCpfConsultadoRef.current = cpfLimpo;
      } else if (error.response?.status === 400) {
        setResultadoBuscaCpf(null);
        setCpfNaoEncontrado(false);
        setErroBuscaCpf(error.response?.data?.error || 'CPF inválido para consulta');
        ultimoCpfConsultadoRef.current = '';
      } else {
        setResultadoBuscaCpf(null);
        setCpfNaoEncontrado(false);
        setErroBuscaCpf(error.response?.data?.error || 'Erro ao consultar CPF cadastrado');
        ultimoCpfConsultadoRef.current = '';
      }
    } finally {
      setBuscandoCpfExistente(false);
    }
  };

  const handleCpfAprovacaoChange = (value) => {
    const formatted = formatarCPF(value);
    const cpfLimpo = normalizarCPF(formatted);

    setFormAprovacao((prev) => ({ ...prev, cpf: formatted }));

    if (cpfLimpo.length < 11) {
      if (buscaCpfTimeoutRef.current) {
        clearTimeout(buscaCpfTimeoutRef.current);
      }
      setResultadoBuscaCpf(null);
      setCpfNaoEncontrado(false);
      setErroBuscaCpf('');
      ultimoCpfConsultadoRef.current = '';
      return;
    }

    if (buscaCpfTimeoutRef.current) {
      clearTimeout(buscaCpfTimeoutRef.current);
    }
    buscaCpfTimeoutRef.current = setTimeout(() => {
      buscarCpfExistente(formatted);
    }, 350);
  };

  const handleCpfAprovacaoBlur = () => {
    buscarCpfExistente(formAprovacao.cpf);
  };

  const usarDadosCadastrados = () => {
    if (!resultadoBuscaCpf) return;

    setFormAprovacao((prev) => ({
      ...prev,
      cpf: resultadoBuscaCpf.cpf ? formatarCPF(resultadoBuscaCpf.cpf) : prev.cpf,
      telefone: resultadoBuscaCpf.telefone || prev.telefone
    }));

    toast.success('Dados cadastrados aplicados ao formulário');
  };

  const fetchPreReservas = async () => {
    try {
      const params = new URLSearchParams();
      if (filtroStatus !== 'todos') {
        params.set('status', filtroStatus);
      }
      params.set('_t', String(Date.now()));
      const response = await api.get(`/api/pre-reservas?${params.toString()}`);
      setPreReservas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar pré-reservas');
    } finally {
      setLoading(false);
    }
  };

  const handleAtualizar = async () => {
    setRefreshing(true);
    try {
      await fetchPreReservas();
    } finally {
      setRefreshing(false);
    }
  };

  const handleVerDetalhes = async (id) => {
    try {
      const response = await api.get(`/api/pre-reservas/${id}`);
      setPreReservaSelecionada(response.data);
      // Extrair número de parcelas se existir nas observações
      let numeroParcelas = '';
      if (response.data.forma_pagamento && formaIndicaCarne(response.data.forma_pagamento)) {
        const parcelasMatch = response.data.observacoes?.match(/Parcelas: (\d+)x/i);
        if (parcelasMatch) {
          numeroParcelas = parcelasMatch[1];
        }
      }

      setFormAprovacao({
        forma_pagamento: response.data.forma_pagamento || '',
        valor: response.data.valor || response.data.valor_viagem || '',
        status_pagamento: 'pendente',
        cpf: response.data.cpf || '',
        telefone: response.data.telefone || '',
        numero_parcelas: numeroParcelas
      });
      setResultadoBuscaCpf(null);
      setCpfNaoEncontrado(false);
      setErroBuscaCpf('');
      ultimoCpfConsultadoRef.current = '';
      setModalOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes da pré-reserva');
    }
  };

  const handleAprovar = async () => {
    if (!formAprovacao.forma_pagamento) {
      toast.error('Selecione a forma de pagamento');
      return;
    }

    if (!formAprovacao.valor || parseFloat(formAprovacao.valor) <= 0) {
      toast.error('Informe o valor da passagem');
      return;
    }

    if (!formAprovacao.cpf || formAprovacao.cpf.trim() === '') {
      toast.error('Informe o CPF do cliente');
      return;
    }

    if (!formAprovacao.telefone || formAprovacao.telefone.trim() === '') {
      toast.error('Informe o telefone do cliente');
      return;
    }

    // Validar número de parcelas se for Carnê
    if (formaIndicaCarne(formAprovacao.forma_pagamento) && (!formAprovacao.numero_parcelas || formAprovacao.numero_parcelas === '')) {
      toast.error('Informe o número de parcelas para pagamento em Carnê');
      return;
    }

    setProcessando(true);

    try {
      // Atualizar dados da pré-reserva se necessário
      const updates = {};
      if (formAprovacao.cpf !== preReservaSelecionada.cpf) {
        updates.cpf = formAprovacao.cpf;
      }
      if (formAprovacao.telefone !== preReservaSelecionada.telefone) {
        updates.telefone = formAprovacao.telefone;
      }
      
      if (Object.keys(updates).length > 0) {
        await api.put(`/api/pre-reservas/${preReservaSelecionada.id}`, updates);
      }

      const dadosAprovacao = {
        forma_pagamento: formAprovacao.forma_pagamento,
        valor: parseFloat(formAprovacao.valor),
        status_pagamento: formAprovacao.status_pagamento
      };

      // Se for Carnê e tiver número de parcelas, incluir
      if (formaIndicaCarne(formAprovacao.forma_pagamento) && formAprovacao.numero_parcelas) {
        dadosAprovacao.numero_parcelas = parseInt(formAprovacao.numero_parcelas);
      }

      await api.post(`/api/pre-reservas/${preReservaSelecionada.id}/aprovar`, dadosAprovacao);

      toast.success('Pré-reserva aprovada e passageiro criado com sucesso!');
      setModalOpen(false);
      fetchPreReservas();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao aprovar pré-reserva');
    } finally {
      setProcessando(false);
    }
  };

  const handleRejeitar = async () => {
    const motivo = prompt('Informe o motivo da rejeição (opcional):');
    
    setProcessando(true);

    try {
      await api.post(`/api/pre-reservas/${preReservaSelecionada.id}/rejeitar`, {
        motivo: motivo || 'Rejeitada pela administração'
      });

      toast.success('Pré-reserva rejeitada com sucesso');
      setModalOpen(false);
      fetchPreReservas();
    } catch (error) {
      toast.error('Erro ao rejeitar pré-reserva');
    } finally {
      setProcessando(false);
    }
  };

  const handleIndeciso = async () => {
    const observacoes = prompt('Observações sobre o cliente indeciso (opcional):');
    
    setProcessando(true);

    try {
      await api.post(`/api/pre-reservas/${preReservaSelecionada.id}/indeciso`, {
        observacoes: observacoes || 'Cliente indeciso - entrar em contato posteriormente'
      });

      toast.success('Pré-reserva marcada como indeciso com sucesso');
      setModalOpen(false);
      fetchPreReservas();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao marcar pré-reserva como indeciso');
    } finally {
      setProcessando(false);
    }
  };

  const handleReativar = async () => {
    if (!window.confirm('Deseja reativar esta pré-reserva? Ela voltará para o status "Pendente" e poderá ser aprovada normalmente.')) {
      return;
    }

    setProcessando(true);

    try {
      await api.post(`/api/pre-reservas/${preReservaSelecionada.id}/reativar`);

      toast.success('Pré-reserva reativada com sucesso');
      setModalOpen(false);
      fetchPreReservas();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao reativar pré-reserva');
    } finally {
      setProcessando(false);
    }
  };

  const handleExcluir = async (id, nome) => {
    if (!window.confirm(`Tem certeza que deseja excluir a pré-reserva de ${nome}?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await api.delete(`/api/pre-reservas/${id}`);
      toast.success('Pré-reserva excluída com sucesso');
      fetchPreReservas();
      if (modalOpen && preReservaSelecionada?.id === id) {
        setModalOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir pré-reserva');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800';
      case 'aprovada':
        return 'bg-green-100 text-green-800';
      case 'rejeitada':
        return 'bg-red-100 text-red-800';
      case 'indeciso':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pendente':
        return 'Pendente';
      case 'aprovada':
        return 'Aprovada';
      case 'rejeitada':
        return 'Rejeitada';
      case 'indeciso':
        return 'Indeciso';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Pré-Reservas</h1>
          <p className="text-white/70 mt-1">Gerencie as solicitações de reserva dos clientes</p>
        </div>
        <button
          type="button"
          onClick={handleAtualizar}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors disabled:opacity-60 disabled:cursor-wait"
        >
          <FiRefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Atualizando…' : 'Atualizar'}
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-sky-50 rounded-lg p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Filtrar por status:</label>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
          >
            <option value="todos">Todos</option>
            <option value="pendente">Pendentes</option>
            <option value="aprovada">Aprovadas</option>
            <option value="rejeitada">Rejeitadas</option>
            <option value="indeciso">Indecisos</option>
          </select>
        </div>
      </div>

      {/* Lista de Pré-Reservas */}
      <div className="bg-sky-50 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Viagem
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Solicitação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {preReservas.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Nenhuma pré-reserva encontrada
                  </td>
                </tr>
              ) : (
                preReservas.map((preReserva) => (
                  <tr key={preReserva.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{preReserva.nome}</div>
                      <div className="text-sm text-gray-500">{preReserva.cpf || 'Não informado'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {preReserva.telefone ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-900 font-medium">{preReserva.telefone}</span>
                          <a 
                            href={`tel:${preReserva.telefone.replace(/\D/g, '')}`}
                            className="text-blue-600 hover:text-blue-800"
                            title="Ligar para este número"
                          >
                            <FiPhone size={16} />
                          </a>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Não informado</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{preReserva.viagem_titulo}</div>
                      <div className="text-sm text-gray-500">
                        {preReserva.destino} - {formatarData(preReserva.data_ida)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {preReserva.assento_numero} - {preReserva.assento_fileira} {preReserva.assento_lado}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(preReserva.created_at).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(preReserva.status)}`}>
                        {getStatusLabel(preReserva.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleVerDetalhes(preReserva.id)}
                          className="text-sky-600 hover:text-sky-900"
                          title="Ver detalhes"
                        >
                          <FiEye size={18} />
                        </button>
                        <button
                          onClick={() => handleExcluir(preReserva.id, preReserva.nome)}
                          className="text-red-600 hover:text-red-900"
                          title="Excluir pré-reserva"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Detalhes */}
      {modalOpen && preReservaSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Detalhes da Pré-Reserva</h2>
                <button
                  onClick={() => setModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Informações do Cliente */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Dados do Cliente</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <FiUser className="text-gray-500" size={18} />
                      <span className="text-sm text-gray-700">{preReservaSelecionada.nome}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">CPF: {preReservaSelecionada.cpf}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiPhone className="text-gray-500" size={18} />
                      <span className="text-sm text-gray-700 font-medium">{preReservaSelecionada.telefone || 'Não informado'}</span>
                      {preReservaSelecionada.telefone && (
                        <a 
                          href={`tel:${preReservaSelecionada.telefone.replace(/\D/g, '')}`}
                          className="text-blue-600 hover:text-blue-800 text-xs underline"
                          title="Ligar para este número"
                        >
                          (Ligar)
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <FiMail className="text-gray-500" size={18} />
                      <span className="text-sm text-gray-700">{preReservaSelecionada.usuario_email || 'Não informado'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiMapPin className="text-gray-500" size={18} />
                      <div className="text-sm text-gray-700">
                        {preReservaSelecionada.endereco && (
                          <div>{preReservaSelecionada.endereco}</div>
                        )}
                        {preReservaSelecionada.bairro && (
                          <div>Bairro: {preReservaSelecionada.bairro}</div>
                        )}
                        <div>{preReservaSelecionada.cidade || 'Cidade não informada'}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Informações da Viagem */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Viagem</h3>
                  <div className="space-y-2">
                    <div className="text-sm text-gray-700">
                      <strong>Título:</strong> {preReservaSelecionada.viagem_titulo}
                    </div>
                    <div className="text-sm text-gray-700">
                      <strong>Destino:</strong> {preReservaSelecionada.destino}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <FiCalendar className="text-gray-500" size={18} />
                      <strong>Data:</strong> {formatarData(preReservaSelecionada.data_ida)}
                    </div>
                    <div className="text-sm text-gray-700">
                      <strong>Assento:</strong> {preReservaSelecionada.assento_numero} - {preReservaSelecionada.assento_fileira} {preReservaSelecionada.assento_lado}
                    </div>
                  </div>
                </div>

                {/* Formulário de Aprovação */}
                {(preReservaSelecionada.status === 'pendente' || preReservaSelecionada.status === 'indeciso') && (
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3">Finalizar Reserva</h3>
                    <div className="space-y-4">
                      {(!preReservaSelecionada.cpf || preReservaSelecionada.cpf.trim() === '') && (
                        <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                          <p className="text-sm text-red-800 font-medium mb-2">⚠️ CPF não informado</p>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              CPF do Cliente *
                            </label>
                            <input
                              type="text"
                              value={formAprovacao.cpf}
                              onChange={(e) => handleCpfAprovacaoChange(e.target.value)}
                              onBlur={handleCpfAprovacaoBlur}
                              maxLength="14"
                              className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white text-gray-800"
                              placeholder="000.000.000-00"
                              required
                            />
                          </div>
                        </div>
                      )}

                      {preReservaSelecionada.cpf && preReservaSelecionada.cpf.trim() !== '' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            CPF do Cliente
                          </label>
                          <input
                            type="text"
                            value={formAprovacao.cpf}
                            onChange={(e) => handleCpfAprovacaoChange(e.target.value)}
                            onBlur={handleCpfAprovacaoBlur}
                            maxLength="14"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                            placeholder="000.000.000-00"
                          />
                        </div>
                      )}

                      {buscandoCpfExistente && (
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                          <p className="text-sm text-blue-800">Consultando CPF na base de passageiros...</p>
                        </div>
                      )}

                      {erroBuscaCpf && (
                        <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                          <p className="text-sm text-red-800">{erroBuscaCpf}</p>
                        </div>
                      )}

                      {resultadoBuscaCpf && (
                        <div className="bg-amber-50 border border-amber-300 p-3 rounded-lg">
                          <p className="text-sm font-semibold text-amber-900 mb-2">CPF já cadastrado na base</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700 mb-3">
                            <div><strong>Nome:</strong> {resultadoBuscaCpf.nome || 'Não informado'}</div>
                            <div><strong>Telefone:</strong> {resultadoBuscaCpf.telefone || 'Não informado'}</div>
                            <div><strong>Cidade:</strong> {resultadoBuscaCpf.cidade || 'Não informado'}</div>
                            <div><strong>Email:</strong> {resultadoBuscaCpf.email || 'Não informado'}</div>
                            <div><strong>Endereço:</strong> {resultadoBuscaCpf.endereco || 'Não informado'}</div>
                            <div><strong>Bairro:</strong> {resultadoBuscaCpf.bairro || 'Não informado'}</div>
                            <div><strong>RG:</strong> {resultadoBuscaCpf.rg || 'Não informado'}</div>
                            <div>
                              <strong>Histórico:</strong> {Array.isArray(resultadoBuscaCpf.historico_viagens) ? `${resultadoBuscaCpf.historico_viagens.length} viagem(ns)` : 'Não informado'}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={usarDadosCadastrados}
                            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                          >
                            Usar dados cadastrados
                          </button>
                        </div>
                      )}

                      {cpfNaoEncontrado && !buscandoCpfExistente && !erroBuscaCpf && (
                        <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg">
                          <p className="text-sm text-gray-700">CPF não cadastrado na base. Pode seguir normalmente com o preenchimento atual.</p>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Telefone para Contato *
                        </label>
                        <div className="relative">
                          <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                          <input
                            type="tel"
                            value={formAprovacao.telefone}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              let formatted = value;
                              if (value.length <= 10) {
                                formatted = value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
                              } else {
                                formatted = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
                              }
                              setFormAprovacao({ ...formAprovacao, telefone: formatted });
                            }}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                            placeholder="(00) 00000-0000"
                            maxLength="15"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Forma de Pagamento *
                        </label>
                        <select
                          value={formAprovacao.forma_pagamento}
                          onChange={(e) => setFormAprovacao({ ...formAprovacao, forma_pagamento: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                          required
                        >
                          <option value="">Selecione...</option>
                          {FORMAS_PAGAMENTO.map((label) => (
                            <option key={label} value={label}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valor da Passagem *
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={formAprovacao.valor}
                            onChange={(e) => setFormAprovacao({ ...formAprovacao, valor: e.target.value })}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                            placeholder={preReservaSelecionada.valor_viagem || "0.00"}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status do Pagamento
                        </label>
                        <select
                          value={formAprovacao.status_pagamento}
                          onChange={(e) => setFormAprovacao({ ...formAprovacao, status_pagamento: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        >
                          <option value="pendente">Pendente</option>
                          <option value="pago">Pago</option>
                        </select>
                      </div>

                      {/* Campo de parcelas - aparece apenas quando for Carnê */}
                      {formaIndicaCarne(formAprovacao.forma_pagamento) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Número de Parcelas (até 10x)
                          </label>
                          <select
                            value={formAprovacao.numero_parcelas}
                            onChange={(e) => setFormAprovacao({ ...formAprovacao, numero_parcelas: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                          >
                            <option value="">Selecione...</option>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                              <option key={num} value={num}>{num}x</option>
                            ))}
                          </select>
                          {formAprovacao.numero_parcelas && formAprovacao.valor && (
                            <p className="text-xs text-gray-500 mt-1">
                              Valor por parcela: R$ {((parseFloat(formAprovacao.valor) || 0) / parseInt(formAprovacao.numero_parcelas)).toFixed(2)}
                            </p>
                          )}
                        </div>
                      )}

                      <div className="space-y-3">
                        <div className="flex gap-3">
                          <button
                            onClick={handleAprovar}
                            disabled={processando || !formAprovacao.forma_pagamento || !formAprovacao.valor || !formAprovacao.cpf || formAprovacao.cpf.trim() === '' || !formAprovacao.telefone || formAprovacao.telefone.trim() === '' || (formaIndicaCarne(formAprovacao.forma_pagamento) && !formAprovacao.numero_parcelas)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiCheck size={18} />
                            {processando ? 'Processando...' : 'Aprovar e Criar Passageiro'}
                          </button>
                          <button
                            onClick={handleIndeciso}
                            disabled={processando}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiClock size={18} />
                            Indeciso
                          </button>
                          <button
                            onClick={handleRejeitar}
                            disabled={processando}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiX size={18} />
                            Rejeitar
                          </button>
                        </div>
                        <button
                          onClick={() => handleExcluir(preReservaSelecionada.id, preReservaSelecionada.nome)}
                          disabled={processando}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FiTrash2 size={18} />
                          Excluir Pré-Reserva
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Status Aprovada - apenas mostra informações */}
                {preReservaSelecionada.status === 'aprovada' && (
                  <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                    <p className="text-sm font-medium">
                      Status: <span className={getStatusColor(preReservaSelecionada.status)}>
                        {getStatusLabel(preReservaSelecionada.status)}
                      </span>
                    </p>
                    {preReservaSelecionada.observacoes && (
                      <p className="text-sm text-gray-700 mt-2">{preReservaSelecionada.observacoes}</p>
                    )}
                    <div className="mt-4">
                      <button
                        onClick={() => handleExcluir(preReservaSelecionada.id, preReservaSelecionada.nome)}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <FiTrash2 size={18} />
                        Excluir Pré-Reserva
                      </button>
                    </div>
                  </div>
                )}

                {/* Status Rejeitada - permite reativar */}
                {preReservaSelecionada.status === 'rejeitada' && (
                  <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                    <p className="text-sm font-medium mb-2">
                      Status: <span className={getStatusColor(preReservaSelecionada.status)}>
                        {getStatusLabel(preReservaSelecionada.status)}
                      </span>
                    </p>
                    {preReservaSelecionada.observacoes && (
                      <p className="text-sm text-gray-700 mb-4">{preReservaSelecionada.observacoes}</p>
                    )}
                    <p className="text-sm text-gray-600 mb-4">
                      Esta pré-reserva foi rejeitada. Você pode reativá-la para que o cliente possa ser contatado novamente e a reserva possa ser aprovada.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleReativar}
                        disabled={processando}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FiRotateCw size={18} />
                        Reativar Pré-Reserva
                      </button>
                      <button
                        onClick={() => handleExcluir(preReservaSelecionada.id, preReservaSelecionada.nome)}
                        disabled={processando}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FiTrash2 size={18} />
                        Excluir
                      </button>
                    </div>
                  </div>
                )}

                {/* Status Indeciso - permite reativar */}
                {preReservaSelecionada.status === 'indeciso' && (
                  <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                    <p className="text-sm font-medium mb-2">
                      Status: <span className={getStatusColor(preReservaSelecionada.status)}>
                        {getStatusLabel(preReservaSelecionada.status)}
                      </span>
                    </p>
                    {preReservaSelecionada.observacoes && (
                      <p className="text-sm text-gray-700 mb-4">{preReservaSelecionada.observacoes}</p>
                    )}
                    <p className="text-sm text-gray-600 mb-4">
                      Cliente indeciso. Você pode entrar em contato posteriormente e, se ele aceitar, reativar esta pré-reserva para aprovação.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleReativar}
                        disabled={processando}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FiRotateCw size={18} />
                        Reativar Pré-Reserva
                      </button>
                      <button
                        onClick={() => handleExcluir(preReservaSelecionada.id, preReservaSelecionada.nome)}
                        disabled={processando}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FiTrash2 size={18} />
                        Excluir
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreReservas;
